import { Field } from '@campfire/field/lib';
import { TabletButton } from '@campfire/tablet-button/lib';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@material-ui/core';
import { Form, Formik, useFormikContext } from 'formik';
import React, { memo, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { object as YupObject, string as YupString } from 'yup';
import { FormikTaskBuilderValues } from '../../admin-console-volunteer-profile-model';
import { PageBreakTaskItemData } from '../FormBuilderDialogContext';

const validationSchema = YupObject().shape({
  title: YupString().required('Please give the page a name'),
});

interface Props {
  open: boolean;
  close: () => void;
  data: PageBreakTaskItemData | null;
}

type PageBreakValues = {
  taskItemId: string;
  title: string;
  order: number;
};

export const PageBreakTaskItemDialog = memo((props: Props) => {
  const { open, close, data } = props;
  const formikContext = useFormikContext<FormikTaskBuilderValues>();
  const { values } = formikContext;
  const { taskItems } = values;

  const title = useMemo(() => (data ? 'Edit Page' : 'Add Page Break'), [data]);

  const initialValues: PageBreakValues = useMemo(
    () => ({
      title: data?.title ?? '',
      order: data?.order ?? taskItems.allIds.length,
      taskItemId: data?.taskItemId ?? `new-${uuidv4()}`,
      __typename: 'VOLUNTEER_TaskItemPageBreakType',
    }),
    [data, open]
  );

  const handleSubmit = (vals: PageBreakValues) => {
    return data ? editPageBreak(vals) : addPageBreak(vals);
  };

  const addPageBreak = (vals: PageBreakValues) => {
    formikContext.setFieldValue('taskItems', {
      ...taskItems,
      allIds: taskItems.allIds.concat(vals.taskItemId),
      byId: {
        ...taskItems.byId,
        [vals.taskItemId]: vals,
      },
    });
    close();
  };

  const editPageBreak = (vals: PageBreakValues) => {
    formikContext.setFieldValue('taskItems', {
      ...taskItems,
      byId: {
        ...taskItems.byId,
        [vals.taskItemId]: vals,
      },
    });
    close();
  };

  const removePageBreak = () => {
    formikContext.setFieldValue('taskItems', {
      ...taskItems,
      allIds: taskItems.allIds.filter((itemId: string) => itemId !== initialValues.taskItemId),
    });
    close();
  };

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ submitForm }) => (
          <Form>
            <DialogTitle>
              <Typography variant='h6'>{title}</Typography>
              <Typography variant='body2' color='textSecondary'>
                {'Everything after this break starts a new page. Pages can hold instructions only, with no questions.'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Field name='title' label='Page name' required fullWidth variant='outlined' helper autoFocus />
            </DialogContent>
            <DialogActions>
              <Grid container justify='flex-end'>
                {data ? (
                  <Grid item xs>
                    <TabletButton variant='outlined' color='error' onClick={removePageBreak}>
                      {'Delete'}
                    </TabletButton>
                  </Grid>
                ) : null}
                <Grid item>
                  <TabletButton onClick={close}>{'Cancel'}</TabletButton>
                </Grid>
                <Grid item>
                  <TabletButton type='button' color='primary' variant='contained' onClick={submitForm}>
                    {'Done'}
                  </TabletButton>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
});
